import styled from "@emotion/styled";
import classNames from "classnames";
import { motion, useMotionValue } from "motion/react";
import { useRef, useState, type CSSProperties } from "react";

import { DragDropProvider, useDraggable, type DragMoveEvent } from "@skilitics/drag-drop";

import { asPixels, getNumericalValue } from "../utilities";

type Props = {
  width: string;
  setWidth: (width: string) => void;
  minWidth: number;
  hidden?: boolean;
};

export const GridSplit = ({ width, setWidth, hidden, minWidth }: Props) => {
  const [gridSplitActive, setGridSplitActive] = useState(false);
  const dragStartWidth = useRef<number>(getNumericalValue(width));

  const gridSplitStyles = {
    display: hidden ? "none" : "flex",
    left: hidden ? 0 : getNumericalValue(width),
    opacity: useMotionValue(hidden ? 0 : 1),
    pointerEvents: hidden ? "none" : ("auto" as CSSProperties["pointerEvents"]),
  };

  const handleSplitBegin = () => {
    setGridSplitActive(true);
    dragStartWidth.current = getNumericalValue(width);
  };

  const handleSplitDrag = ({ operation: { transform } }: DragMoveEvent) => {
    if (!transform) return;
    const newLeft = dragStartWidth.current + transform.x;
    if (newLeft < minWidth) return;
    setWidth(asPixels(newLeft));
  };

  const handleSplitEnd = () => {
    setGridSplitActive(false);
  };

  return (
    <DragDropProvider onDragEnd={handleSplitEnd} onDragStart={handleSplitBegin} onDragMove={handleSplitDrag}>
      <Container style={gridSplitStyles} className={classNames({ gridSplitActive })}>
        <GridSplitHandle />
      </Container>
    </DragDropProvider>
  );
};

const GridSplitHandle = () => {
  const { ref } = useDraggable({ id: "grid-split-handle" });
  return (
    <GridSplitHandleContainer ref={ref}>
      <Handle />
    </GridSplitHandleContainer>
  );
};

const Handle = styled.div`
  width: 4px;
  height: 100%;

  background-color: ${({ theme }) => theme.generalTextIconVariant};

  opacity: 0;

  transition: all 200ms ease-in-out;
`;

const GridSplitHandleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: 100%;
  height: 100%;

  cursor: ew-resize;

  &:hover {
    ${Handle} {
      opacity: 0.4;
    }
  }
`;

const Container = motion.create(styled.div`
  position: absolute;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  top: 0;
  left: 0;

  width: 14px;
  height: 100%;

  &.gridSplitActive ${Handle} {
    opacity: 0.6;
    background-color: ${({ theme }) => theme.primary};
  }
`);
